/**
 * The one place a connector's mark is declared.
 *
 * Keyed by connector id — the only identifier every surface already shares:
 * the unfurl response returns it, GET /api/connections/catalog returns it,
 * and the slash catalog uses it as its command id.
 *
 * The mark itself is `public/connector-icons/<id>.svg`, so the filename is the
 * declaration and this list is only what we ship. Keeping the list lets an
 * unknown id go straight to the glyph instead of firing a 404 first.
 *
 * Covers every id the backend catalog can return, so nothing falls back to a
 * glyph in Settings; the catalog's own iconUrl is no longer read. A couple of
 * ids are frontend-only — "pdf", and "google-meet" for the meeting rows.
 *
 * Each mark is the brand's own logo where one was obtainable, in its real
 * colours: 34 from SVGL, VectorLogoZone, or the vendor's own favicon.svg.
 *
 * Eleven stay on Simple Icons, which is monochrome by design, because no
 * usable colour source exists yet: confluence, google-forms, jira, lucid,
 * miro, mixpanel, notion, pagerduty, sketch, soundcloud, x, and calendly
 * whose SVGL asset was not its mark. Six carry a
 * brand-coloured initial: box, graphy, pdf, tableau, typeform, whimsical.
 * Both groups are one file drop away from the official asset.
 *
 * Rejected on inspection, so the reasons are not rediscovered: a favicon
 * from a multi-product domain returns the parent's mark (jira.png and
 * confluence.png came back byte-identical Atlassian logos), Google's
 * favicon service ignores the size you ask for (lucid arrived 16px), and
 * an SVG drawn with currentColor renders black inside an <img>.
 */

const SHIPPED = new Set([
  "airtable",
  "asana",
  "box",
  "calendly",
  "canva",
  "claude",
  "clickup",
  "codepen",
  "codesandbox",
  "confluence",
  "dropbox",
  "figma",
  "github",
  "gitlab",
  "gmail",
  "google-calendar",
  "intercom",
  "google-drive",
  "google-forms",
  "google-maps",
  "google-meet",
  "graphy",
  "jira",
  "linear",
  "loom",
  "lottiefiles",
  "lucid",
  "miro",
  "mixpanel",
  "notion",
  "onedrive",
  "outlook",
  "pagerduty",
  "pdf",
  "pinterest",
  "replit",
  "salesforce",
  "sentry",
  "sketch",
  "slack",
  "soundcloud",
  "spotify",
  "streamlit",
  "tableau",
  "teams",
  "trello",
  "typeform",
  "vimeo",
  "whimsical",
  "x",
  "youtube",
  "zoom",
]);

/**
 * Marks that vanish on the dark surface (#191919) — all of these sit at or
 * below 1.4:1 against it, which is not low contrast so much as invisible.
 * Each ships a lighter twin at `<id>-dark.svg`.
 *
 * Shorter than it was: a real logo usually carries its own contrast, so
 * codesandbox and slack stopped needing one the moment they gained their
 * colours. Saturated marks are left alone — jira sits at 2.58:1 and reads
 * fine, and contrast rules exempt logotypes anyway.
 */
const DARK_VARIANTS = new Set([
  "codepen",
  "codesandbox",
  "confluence",
  "github",
  "intercom",
  "lucid",
  "miro",
  "notion",
  "sentry",
  "typeform",
  "x",
]);

/** Path to a connector's mark, or null when we ship none for it. */
export function connectorIconPath(id: string): string | null {
  return SHIPPED.has(id) ? `/connector-icons/${id}.svg` : null;
}

/** Lighter twin for the dark surface, or null when the mark reads as it is. */
export function connectorIconDarkPath(id: string): string | null {
  return DARK_VARIANTS.has(id) ? `/connector-icons/${id}-dark.svg` : null;
}
